import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function PythonTimestamp() {
  const { t, i18n } = useTranslation(['programming', 'common']);
  const [copySuccess, setCopySuccess] = useState('');

  // Update SEO
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('pythonTimestamp', currentLang);
    updateSEOTags(seoConfig, currentLang);
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  // Copy to clipboard
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(id);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const CodeBlock = ({ code, language = 'python', id }: { code: string; language?: string; id: string }) => (
    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
      >
        {copySuccess === id ? t('common:button.copied') : t('common:button.copy')}
      </button>
      <pre className="text-green-400 text-sm font-mono whitespace-pre overflow-x-auto">
        {code}
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 py-4 md:py-8">
        {/* Page Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            🐍 {t('python.mainTitleSEO')}
          </h1>
          <p className="text-lg text-gray-600">{t('python.subtitle')}</p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <p className="text-gray-700 leading-relaxed">{t('python.intro')}</p>
        </div>

        {/* How to Import Timestamp Modules */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Import Timestamp Modules in Python</h2>
          <p className="text-gray-600 mb-6">
            Before working with timestamps, you need to import the necessary Python modules. Here are the main modules for handling Unix timestamps:
          </p>

          <div className="space-y-6">
            {/* time module */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">time</span>
                Basic Unix Timestamp Operations
              </h3>
              <CodeBlock
                id="import-1"
                code={`# Import time module for basic timestamp operations
import time

# Get current Unix timestamp
timestamp = time.time()
print(timestamp)  # 1735948800.123456

# time module is built-in, no installation required
# Best for: Performance-critical timestamp generation`}
              />
            </div>

            {/* datetime module */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">datetime</span>
                Advanced Date and Time Handling
              </h3>
              <CodeBlock
                id="import-2"
                code={`# Import datetime for advanced timestamp operations
from datetime import datetime, timedelta, timezone

# Get current timestamp
timestamp = int(datetime.now().timestamp())

# datetime is also built-in
# Best for: Date arithmetic, formatting, timezone handling`}
              />
            </div>

            {/* calendar module */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">calendar</span>
                UTC Timestamp Conversion
              </h3>
              <CodeBlock
                id="import-3"
                code={`# Import calendar for UTC timestamp operations
import calendar
from datetime import datetime

# Convert UTC datetime to timestamp
utc_dt = datetime.utcnow()
timestamp = calendar.timegm(utc_dt.timetuple())

# Best for: UTC timestamp conversions`}
              />
            </div>

            {/* Quick reference */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">📌 Quick Import Reference</h4>
              <CodeBlock
                id="import-4"
                code={`# All-in-one import for timestamp operations
import time
from datetime import datetime, timedelta, timezone
import calendar

# Optional: For timezone handling (Python 3.9+)
from zoneinfo import ZoneInfo

# Optional: For older Python versions
import pytz  # pip install pytz`}
              />
            </div>
          </div>
        </div>

        {/* Get Current Timestamp */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Get Current Unix Timestamp in Python (Seconds & Milliseconds)</h2>
          <p className="text-gray-600 mb-6">
            Learn how to get the current Unix timestamp in Python. You can retrieve timestamps in seconds (10 digits) or milliseconds (13 digits) depending on your needs.
          </p>

          <div className="space-y-6">
            {/* Method 1: time.time() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('python.method1')}
              </h3>
              <CodeBlock
                id="python-1"
                code={`import time

# Get current timestamp (returns float with microseconds)
timestamp = time.time()
print(timestamp)  # Output: 1735948800.123456

# Get integer timestamp (seconds only)
timestamp_int = int(time.time())
print(timestamp_int)  # Output: 1735948800

# Get millisecond timestamp
timestamp_ms = int(time.time() * 1000)
print(timestamp_ms)  # Output: 1735948800123`}
              />
              <p className="mt-2 text-sm text-gray-600">💡 time.time() is the fastest method for getting current timestamp</p>
            </div>

            {/* Method 2: datetime.now() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('python.method2')}
              </h3>
              <CodeBlock
                id="python-2"
                code={`from datetime import datetime

# Get current datetime and convert to timestamp
now = datetime.now()
timestamp = int(now.timestamp())
print(timestamp)  # Output: 1735948800

# One-liner
timestamp = int(datetime.now().timestamp())
print(timestamp)  # Output: 1735948800`}
              />
            </div>

            {/* Method 3: calendar */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">3</span>
                {t('python.method3')}
              </h3>
              <CodeBlock
                id="python-3"
                code={`import calendar
from datetime import datetime

# Using calendar.timegm() for UTC timestamp
utc_now = datetime.utcnow()
timestamp = calendar.timegm(utc_now.timetuple())
print(timestamp)  # Output: 1735948800`}
              />
            </div>
          </div>
        </div>

        {/* Convert Timestamp to Datetime */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('python.toDatetimeTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('python.toDatetimeDesc')}</p>

          <div className="space-y-6">
            {/* Basic Conversion */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('python.fromTimestamp')}
              </h3>
              <CodeBlock
                id="python-4"
                code={`from datetime import datetime

# Convert timestamp to datetime
timestamp = 1735948800
dt = datetime.fromtimestamp(timestamp)
print(dt)  # Output: 2025-01-04 12:00:00

# Convert from milliseconds
timestamp_ms = 1735948800000
dt = datetime.fromtimestamp(timestamp_ms / 1000)
print(dt)  # Output: 2025-01-04 12:00:00

# UTC timestamp
dt_utc = datetime.utcfromtimestamp(timestamp)
print(dt_utc)  # Output: 2025-01-04 04:00:00 (if local is UTC+8)`}
              />
            </div>

            {/* Format Datetime */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('python.formatDatetime')}
              </h3>
              <CodeBlock
                id="python-5"
                code={`from datetime import datetime

timestamp = 1735948800
dt = datetime.fromtimestamp(timestamp)

# Format as string
formatted = dt.strftime('%Y-%m-%d %H:%M:%S')
print(formatted)  # Output: 2025-01-04 12:00:00

# Different formats
print(dt.strftime('%B %d, %Y'))      # December 19, 2024
print(dt.strftime('%Y/%m/%d %I:%M %p'))  # 2024/12/19 12:00 PM
print(dt.strftime('%A, %B %d, %Y'))  # Thursday, December 19, 2024

# ISO 8601 format
print(dt.isoformat())  # 2024-12-19T17:39:44`}
              />
            </div>

            {/* Timezone-aware */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">3</span>
                {t('python.timezoneAware')}
              </h3>
              <CodeBlock
                id="python-6"
                code={`from datetime import datetime, timezone

# Create timezone-aware datetime from timestamp
timestamp = 1735948800
dt = datetime.fromtimestamp(timestamp, tz=timezone.utc)
print(dt)  # Output: 2025-01-04 04:00:00+00:00

# Convert to local timezone
import pytz
local_tz = pytz.timezone('Asia/Shanghai')
dt_local = datetime.fromtimestamp(timestamp, tz=local_tz)
print(dt_local)  # Output: 2025-01-04 12:00:00+08:00`}
              />
            </div>
          </div>
        </div>

        {/* Convert Datetime to Timestamp */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('python.toTimestampTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('python.toTimestampDesc')}</p>

          <div className="space-y-6">
            {/* Using .timestamp() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('python.usingTimestamp')}
              </h3>
              <CodeBlock
                id="python-7"
                code={`from datetime import datetime

# Convert datetime to timestamp
dt = datetime(2024, 12, 19, 17, 39, 44)
timestamp = int(dt.timestamp())
print(timestamp)  # Output: 1734630584

# From current datetime
now = datetime.now()
timestamp = int(now.timestamp())
print(timestamp)  # Current timestamp`}
              />
            </div>

            {/* Using mktime */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('python.usingMktime')}
              </h3>
              <CodeBlock
                id="python-8"
                code={`import time
from datetime import datetime

# Using time.mktime()
dt = datetime(2024, 12, 19, 17, 39, 44)
timestamp = int(time.mktime(dt.timetuple()))
print(timestamp)  # Output: 1734630584

# Note: mktime() assumes local timezone
# For UTC, use calendar.timegm()
import calendar
timestamp_utc = calendar.timegm(dt.timetuple())
print(timestamp_utc)`}
              />
            </div>
          </div>
        </div>

        {/* Convert Timestamp to String */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Convert Python Timestamp to String</h2>
          <p className="text-gray-600 mb-6">
            Learn how to format Unix timestamps as readable date strings using Python's strftime() method and other formatting techniques.
          </p>

          <div className="space-y-6">
            {/* Basic String Formatting */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded text-sm mr-3">1</span>
                Using strftime() for Custom Formats
              </h3>
              <CodeBlock
                id="python-to-string-1"
                code={`from datetime import datetime

# Convert timestamp to datetime first, then to string
timestamp = 1735948800
dt = datetime.fromtimestamp(timestamp)

# Standard format: YYYY-MM-DD HH:MM:SS
date_str = dt.strftime('%Y-%m-%d %H:%M:%S')
print(date_str)  # 2025-01-04 12:00:00

# Common date formats
print(dt.strftime('%B %d, %Y'))           # January 04, 2025
print(dt.strftime('%m/%d/%Y'))            # 01/04/2025
print(dt.strftime('%d-%b-%Y'))            # 04-Jan-2025
print(dt.strftime('%A, %B %d, %Y'))       # Saturday, January 04, 2025

# Time formats
print(dt.strftime('%H:%M:%S'))            # 12:00:00 (24-hour)
print(dt.strftime('%I:%M:%S %p'))         # 12:00:00 PM (12-hour)
print(dt.strftime('%H:%M'))               # 12:00 (hour:minute only)`}
              />
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  <strong>Common Format Codes:</strong><br/>
                  • %Y = 4-digit year (2025) • %m = Month (01-12) • %d = Day (01-31)<br/>
                  • %H = Hour 24h (00-23) • %I = Hour 12h (01-12) • %M = Minute (00-59)<br/>
                  • %S = Second (00-59) • %p = AM/PM • %A = Weekday name • %B = Month name
                </p>
              </div>
            </div>

            {/* ISO 8601 Format */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded text-sm mr-3">2</span>
                ISO 8601 String Format
              </h3>
              <CodeBlock
                id="python-to-string-2"
                code={`from datetime import datetime

timestamp = 1735948800
dt = datetime.fromtimestamp(timestamp)

# ISO 8601 format (recommended for APIs)
iso_string = dt.isoformat()
print(iso_string)  # 2025-01-04T12:00:00

# ISO format with timezone
from datetime import timezone
dt_utc = datetime.fromtimestamp(timestamp, tz=timezone.utc)
print(dt_utc.isoformat())  # 2025-01-04T04:00:00+00:00

# RFC 3339 format (web standard)
print(dt_utc.strftime('%Y-%m-%dT%H:%M:%S%z'))
# 2025-01-04T04:00:00+0000`}
              />
            </div>

            {/* Quick One-Liners */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded text-sm mr-3">3</span>
                Quick One-Liner Conversions
              </h3>
              <CodeBlock
                id="python-to-string-3"
                code={`from datetime import datetime

timestamp = 1735948800

# One-liner conversions
date_str = datetime.fromtimestamp(timestamp).strftime('%Y-%m-%d')
print(date_str)  # 2025-01-04

time_str = datetime.fromtimestamp(timestamp).strftime('%H:%M:%S')
print(time_str)  # 12:00:00

full_str = datetime.fromtimestamp(timestamp).strftime('%Y-%m-%d %H:%M:%S')
print(full_str)  # 2025-01-04 12:00:00

# Human-readable format
readable = datetime.fromtimestamp(timestamp).strftime('%B %d, %Y at %I:%M %p')
print(readable)  # January 04, 2025 at 12:00 PM`}
              />
            </div>

            {/* From Milliseconds */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded text-sm mr-3">4</span>
                Convert Millisecond Timestamp to String
              </h3>
              <CodeBlock
                id="python-to-string-4"
                code={`from datetime import datetime

# JavaScript-style timestamp (milliseconds)
timestamp_ms = 1735948800000

# Convert milliseconds to seconds, then to string
dt = datetime.fromtimestamp(timestamp_ms / 1000)
date_str = dt.strftime('%Y-%m-%d %H:%M:%S')
print(date_str)  # 2025-01-04 12:00:00

# One-liner
date_str = datetime.fromtimestamp(timestamp_ms / 1000).strftime('%Y-%m-%d %H:%M:%S')
print(date_str)`}
              />
            </div>
          </div>
        </div>

        {/* Timezone Handling */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('python.timezoneTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('python.timezoneDesc')}</p>

          <div className="space-y-6">
            {/* pytz */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('python.pytzExample')}</h3>
              <CodeBlock
                id="python-9"
                code={`import pytz
from datetime import datetime

# Define timezones
utc = pytz.UTC
beijing = pytz.timezone('Asia/Shanghai')
new_york = pytz.timezone('America/New_York')

# Create timezone-aware datetime
dt_utc = datetime.now(utc)
print(dt_utc)

# Convert between timezones
dt_beijing = dt_utc.astimezone(beijing)
print(dt_beijing)  # Beijing time

dt_ny = dt_utc.astimezone(new_york)
print(dt_ny)  # New York time

# Localize naive datetime
naive_dt = datetime(2024, 12, 19, 17, 39, 44)
aware_dt = beijing.localize(naive_dt)
print(aware_dt)  # 2024-12-19 17:39:44+08:00`}
              />
            </div>

            {/* zoneinfo (Python 3.9+) */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('python.zoneinfoExample')}</h3>
              <CodeBlock
                id="python-10"
                code={`from datetime import datetime
from zoneinfo import ZoneInfo

# Using zoneinfo (Python 3.9+)
# More modern and built-in
utc = ZoneInfo('UTC')
beijing = ZoneInfo('Asia/Shanghai')
new_york = ZoneInfo('America/New_York')

# Create timezone-aware datetime
dt_utc = datetime.now(utc)
print(dt_utc)

# Convert to different timezone
dt_beijing = dt_utc.astimezone(beijing)
print(dt_beijing)

# Create from specific timezone
dt = datetime(2024, 12, 19, 17, 39, 44, tzinfo=beijing)
print(dt)  # 2024-12-19 17:39:44+08:00

# Convert to timestamp
timestamp = int(dt.timestamp())
print(timestamp)`}
              />
            </div>
          </div>
        </div>

        {/* Common Patterns */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('python.patternsTitle')}</h2>

          <div className="space-y-6">
            {/* Timestamp Arithmetic */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('python.pattern1.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('python.pattern1.desc')}</p>
              <CodeBlock
                id="python-11"
                code={`from datetime import datetime, timedelta

# Add 7 days to current timestamp
now = datetime.now()
future = now + timedelta(days=7)
future_timestamp = int(future.timestamp())

# Subtract 1 hour
past = now - timedelta(hours=1)
past_timestamp = int(past.timestamp())

# Complex arithmetic
delta = timedelta(days=30, hours=12, minutes=30)
result = now + delta
result_timestamp = int(result.timestamp())`}
              />
            </div>

            {/* Compare Timestamps */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('python.pattern2.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('python.pattern2.desc')}</p>
              <CodeBlock
                id="python-12"
                code={`from datetime import datetime

# Compare two timestamps
ts1 = 1735948800
ts2 = 1735952400

dt1 = datetime.fromtimestamp(ts1)
dt2 = datetime.fromtimestamp(ts2)

# Time difference
diff = dt2 - dt1
print(f"Difference: {diff.total_seconds()} seconds")
print(f"Days: {diff.days}, Seconds: {diff.seconds}")

# Check which is later
if dt1 < dt2:
    print("dt1 is earlier")

# Get absolute difference in hours
hours_diff = abs((dt2 - dt1).total_seconds() / 3600)
print(f"Hours difference: {hours_diff}")`}
              />
            </div>

            {/* Round Timestamps */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('python.pattern3.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('python.pattern3.desc')}</p>
              <CodeBlock
                id="python-13"
                code={`from datetime import datetime

dt = datetime(2024, 12, 19, 17, 39, 44)

# Round to start of day
start_of_day = dt.replace(hour=0, minute=0, second=0, microsecond=0)
print(start_of_day)  # 2024-12-19 00:00:00

# Round to start of hour
start_of_hour = dt.replace(minute=0, second=0, microsecond=0)
print(start_of_hour)  # 2024-12-19 17:00:00

# Round to nearest 15 minutes
minutes = (dt.minute // 15) * 15
rounded = dt.replace(minute=minutes, second=0, microsecond=0)
print(rounded)  # 2024-12-19 17:30:00

# Start of month
start_of_month = dt.replace(day=1, hour=0, minute=0, second=0)`}
              />
            </div>

            {/* Parse String */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('python.pattern4.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('python.pattern4.desc')}</p>
              <CodeBlock
                id="python-14"
                code={`from datetime import datetime

# Parse date string
date_str = "2024-12-19 17:39:44"
dt = datetime.strptime(date_str, "%Y-%m-%d %H:%M:%S")
timestamp = int(dt.timestamp())
print(timestamp)  # 1734630584

# Different formats
formats = {
    "Dec 19, 2024": "%b %d, %Y",
    "19/12/2024": "%d/%m/%Y",
    "2024-12-19T17:39:44": "%Y-%m-%dT%H:%M:%S"
}

for date_str, fmt in formats.items():
    dt = datetime.strptime(date_str, fmt)
    print(f"{date_str} -> {int(dt.timestamp())}")

# ISO 8601 parsing
iso_str = "2024-12-19T17:39:44+08:00"
dt = datetime.fromisoformat(iso_str)
timestamp = int(dt.timestamp())`}
              />
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border-2 border-yellow-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            ⚡ {t('common.bestPractices')}
          </h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">time.time()</code> for performance-critical timestamp generation</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Always use timezone-aware datetimes when dealing with multiple timezones</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Store timestamps in UTC in databases, convert to local timezone for display</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">zoneinfo</code> (Python 3.9+) instead of <code className="bg-gray-100 px-1.5 py-0.5 rounded">pytz</code> for new projects</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">datetime.fromisoformat()</code> for ISO 8601 date parsing</span>
            </li>
          </ul>
        </div>

        {/* Frequently Asked Questions */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            ❓ Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {/* FAQ 1 */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-800 mb-2">
                How do I get timestamp in seconds in Python?
              </h3>
              <p className="text-gray-600 mb-2">
                Use <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">int(time.time())</code> to get the Unix timestamp in seconds (10 digits). This returns an integer representing seconds since January 1, 1970 (Unix epoch).
              </p>
              <CodeBlock
                id="faq-1"
                code={`import time
timestamp_seconds = int(time.time())
print(timestamp_seconds)  # 1735948800`}
              />
            </div>

            {/* FAQ 2 */}
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-gray-800 mb-2">
                What is the difference between time.time() and datetime.now().timestamp()?
              </h3>
              <p className="text-gray-600">
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">time.time()</code> is faster and directly returns a float timestamp.
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">datetime.now().timestamp()</code> creates a datetime object first, then converts it.
                Use <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">time.time()</code> for performance-critical applications and
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">datetime.now().timestamp()</code> when you need datetime operations.
              </p>
            </div>

            {/* FAQ 3 */}
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-gray-800 mb-2">
                How to convert Python timestamp to string format?
              </h3>
              <p className="text-gray-600 mb-2">
                First convert the timestamp to a datetime object using <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">datetime.fromtimestamp()</code>,
                then use <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">strftime()</code> to format it as a string:
              </p>
              <CodeBlock
                id="faq-3"
                code={`from datetime import datetime
timestamp = 1735948800
date_str = datetime.fromtimestamp(timestamp).strftime('%Y-%m-%d %H:%M:%S')
print(date_str)  # 2025-01-04 12:00:00`}
              />
            </div>

            {/* FAQ 4 */}
            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-semibold text-gray-800 mb-2">
                What is a Unix timestamp?
              </h3>
              <p className="text-gray-600">
                A Unix timestamp (also called epoch time or POSIX time) is the number of seconds that have elapsed since January 1, 1970, 00:00:00 UTC.
                It's a universal way to represent time that's independent of timezones. Python's <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">time.time()</code>
                returns this value as a float with microsecond precision.
              </p>
            </div>

            {/* FAQ 5 */}
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="font-semibold text-gray-800 mb-2">
                How do I handle timezone when working with timestamps?
              </h3>
              <p className="text-gray-600 mb-2">
                Always store timestamps in UTC and convert to local timezone for display. Use timezone-aware datetime objects:
              </p>
              <CodeBlock
                id="faq-5"
                code={`from datetime import datetime, timezone
# Get UTC timestamp
utc_dt = datetime.now(timezone.utc)
timestamp = int(utc_dt.timestamp())

# For Python 3.9+, use zoneinfo
from zoneinfo import ZoneInfo
beijing_time = datetime.now(ZoneInfo('Asia/Shanghai'))
print(beijing_time)`}
              />
            </div>

            {/* FAQ 6 */}
            <div className="border-l-4 border-pink-500 pl-4">
              <h3 className="font-semibold text-gray-800 mb-2">
                How to convert milliseconds timestamp to Python datetime?
              </h3>
              <p className="text-gray-600 mb-2">
                Divide the millisecond timestamp by 1000 to get seconds, then use <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">datetime.fromtimestamp()</code>:
              </p>
              <CodeBlock
                id="faq-6"
                code={`from datetime import datetime
# JavaScript-style timestamp (milliseconds)
timestamp_ms = 1735948800000
dt = datetime.fromtimestamp(timestamp_ms / 1000)
print(dt)  # 2025-01-04 12:00:00`}
              />
            </div>
          </div>
        </div>

        {/* Related Tools */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('common:relatedTools', { defaultValue: 'Related Tools' })}
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            <a href="/timestamp-converter" className="block p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Unix Timestamp Converter</h3>
              <p className="text-sm text-gray-600">Convert timestamps online</p>
            </a>
            <a href="/sql-timestamp" className="block p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">SQL Timestamp Guide</h3>
              <p className="text-sm text-gray-600">MySQL, PostgreSQL, Oracle, SQL Server</p>
            </a>
            <a href="/javascript-timestamp" className="block p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">JavaScript Timestamp</h3>
              <p className="text-sm text-gray-600">Date object guide</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
